import React, { useState } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';

import FDAApi from '../../../api/data-sections/fdaApi';
import StandardDataGrid from '../../../components/Common/StandardDataGrid';
import K510Modal from '../../../components/DataSections/FDA/Modal/510kModal';
import EnforcementModal from '../../../components/DataSections/FDA/Modal/EnforcementModal';
import FdaEventModal from '../../../components/DataSections/FDA/Modal/EventModal';
import RecallModal from '../../../components/DataSections/FDA/Modal/RecallModal';
import PmaModal from '../../../components/DataSections/FDA/Modal/PmaModal';
import RegistrationListingModal from '../../../components/DataSections/FDA/Modal/RegistrationListingModal';
import UdiModal from '../../../components/DataSections/FDA/Modal/UdiModal';
import { useSystemError } from '../../../components/Error/SystemErrorContext';
import { Fda510kModel } from '../../../models/data-sections/fda/510kModel';
import { FdaEventModel } from '../../../models/data-sections/fda/eventModel';
import { FdaPmaModel } from '../../../models/data-sections/fda/pmaModel';
import { FdaEnforcementModel } from '../../../models/data-sections/fda/enforcementModel';
import { FdaRecallModel } from '../../../models/data-sections/fda/recallModel';
import { FdaRegistrationListingModel } from '../../../models/data-sections/fda/registrationlistingModel';
import { FdaUdiModel } from '../../../models/data-sections/fda/udiModel';
import { ResponseErrorModel } from '../../../models/system/responseErrorModel';
import { ResponseSuccessModel } from '../../../models/system/responseSuccessModel';
import { SearchesModel } from '../../../models/platform/searchesModel';
import { handleError, mapResponseErrorToSystemError, mapUnknownErrorToSystemError } from '../../../utils/errorUtils';

interface FDAProps {
  searchExecutionId: string;
  k510: Fda510kModel[] | null;
  enforcements: FdaEnforcementModel[] | null;
  events: FdaEventModel[] | null;
  pma: FdaPmaModel[] | null;
  recalls: FdaRecallModel[] | null;
  registration_listings: FdaRegistrationListingModel[] | null;
  udi: FdaUdiModel[] | null;
  isLoading: boolean;
}

const FDA: React.FC<FDAProps> = ({ searchExecutionId,
  k510,
  enforcements,
  events,
  pma,
  recalls,
  registration_listings,
  udi,
  isLoading,
}) => {
  const [didErrorOccur, setDidErrorOccur] = useState(false);
  const { triggerSystemError } = useSystemError();

  const fetchApiFda510kSearch = async (search_execution_id: string, search: SearchesModel): Promise<void> => {
    try {
      const response = await FDAApi.get510kData(search_execution_id, search);
      if (response.success) {
        const data = (response as ResponseSuccessModel).data as Fda510kModel[];
      } else {
        handleError(mapResponseErrorToSystemError(response as ResponseErrorModel), null, setDidErrorOccur, triggerSystemError);
      }
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const fetchApiFdaEventsSearch = async (search_execution_id: string, search: SearchesModel): Promise<void> => {
    try {
      const response = await FDAApi.getEventsData(search_execution_id, search);
      if (response.success) {
        const data = (response as ResponseSuccessModel).data as FdaEventModel[];
      } else {
        handleError(mapResponseErrorToSystemError(response as ResponseErrorModel), null, setDidErrorOccur, triggerSystemError);
      }
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const fetchApiFdaEnforcementsSearch = async (search_execution_id: string, search: SearchesModel): Promise<void> => {
    try {
      const response = await FDAApi.getEnforcementsData(search_execution_id, search);
      if (response.success) {
        const data = (response as ResponseSuccessModel).data as FdaEnforcementModel[];
      } else {
        handleError(mapResponseErrorToSystemError(response as ResponseErrorModel), null, setDidErrorOccur, triggerSystemError);
      }
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const fetchApiFdaPmaSearch = async (search_execution_id: string, search: SearchesModel): Promise<void> => {
    try {
      const response = await FDAApi.getPmaData(search_execution_id, search);
      if (response.success) {
        const data = (response as ResponseSuccessModel).data as FdaPmaModel[];
      } else {
        handleError(mapResponseErrorToSystemError(response as ResponseErrorModel), null, setDidErrorOccur, triggerSystemError);
      }
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const fetchApiFdaRecallSearch = async (search_execution_id: string, search: SearchesModel): Promise<void> => {
    try {
      const response = await FDAApi.getRecallData(search_execution_id, search);
      if (response.success) {
        const data = (response as ResponseSuccessModel).data as FdaRecallModel[];
      } else {
        handleError(mapResponseErrorToSystemError(response as ResponseErrorModel), null, setDidErrorOccur, triggerSystemError);
      }
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const fetchApiFdaRegistrationListingSearch = async (search_execution_id: string, search: SearchesModel): Promise<void> => {
    try {
      const response = await FDAApi.getRegistrationListingsData(search_execution_id, search);
      if (response.success) {
        const data = (response as ResponseSuccessModel).data as FdaRegistrationListingModel[];
      } else {
        handleError(mapResponseErrorToSystemError(response as ResponseErrorModel), null, setDidErrorOccur, triggerSystemError);
      }
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const fetchApiFdaUdiSearch = async (search_execution_id: string, search: SearchesModel): Promise<void> => {
    try {
      const response = await FDAApi.getUdiData(search_execution_id, search);
      if (response.success) {
        const data = (response as ResponseSuccessModel).data as FdaUdiModel[];
      } else {
        handleError(mapResponseErrorToSystemError(response as ResponseErrorModel), null, setDidErrorOccur, triggerSystemError);
      }
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const k510Columns: GridColDef<Fda510kModel>[] = [
    { field: 'k_number', headerName: '510(k) Number', width: 150 },
    { field: 'device_name', headerName: 'Device Name', width: 220 },
    { field: 'applicant', headerName: 'Applicant', width: 200 },
    { field: 'product_code', headerName: 'Product Code', width: 140 },
    { field: 'clearance_type', headerName: 'Clearance Type', width: 160 },
    { field: 'review_advisory_committee', headerName: 'Review Panel', width: 180 },
    { field: 'decision_date', headerName: 'Decision Date', width: 140 },
    { field: 'advisory_committee_description', headerName: 'Advisory Description', width: 220 },
  ];

  const EnforcementColumns: GridColDef<FdaEventModel>[] = [
    { field: 'recall_number', headerName: 'Recall Number', width: 150 },
    { field: 'recall_initiation_date', headerName: 'Recall Date', width: 150 },
    { field: 'recalling_firm', headerName: 'Recalling Firm', width: 200 },
    { field: 'reason_for_recall', headerName: 'Type', width: 600 },
  ];

  const EventColumns: GridColDef<FdaEventModel>[] = [
    { field: 'mdr_report_key', headerName: 'MDR Report Key', width: 180 },
    { field: 'event_type', headerName: 'Event Type', width: 150 },
    { field: 'adverse_event_flag', headerName: 'Adverse Event', width: 150 },
    { field: 'date_of_event', headerName: 'Date of Event', width: 150 },
    { field: 'date_report', headerName: 'Date Reported', width: 150 },
    { field: 'manufacturer_g1_name', headerName: 'Manufacturer', width: 200 },
  ];

  const PmaColumns: GridColDef<FdaPmaModel>[] = [
    { field: 'pma_number', headerName: 'PMA Number', width: 150 },
    { field: 'supplement_number', headerName: 'Supplement #', width: 130 },
    { field: 'trade_name', headerName: 'Trade Name', width: 200 },
    { field: 'generic_name', headerName: 'Generic Name', width: 200 },
    { field: 'applicant', headerName: 'Applicant', width: 200 },
    { field: 'supplement_type', headerName: 'Type', width: 160 },
    { field: 'decision_date', headerName: 'Decision Date', width: 140 },
  ];

  const RecallColumns: GridColDef<FdaRecallModel>[] = [
    { field: 'product_res_number', headerName: 'Res. Number', width: 160 },
    { field: 'recall_status', headerName: 'Status', width: 130 },
    { field: 'recalling_firm', headerName: 'Recalling Firm', width: 200 },
    { field: 'reason_for_recall', headerName: 'Reason', width: 600 },
    { field: 'event_date_initiated', headerName: 'Initiated', width: 140 },
    { field: 'event_date_posted', headerName: 'Posted', width: 140 },
  ];

  const RegistrationListingColumns: GridColDef<FdaRegistrationListingModel>[] = [
    { field: 'proprietary_name', headerName: 'Proprietary Name', width: 220 },
    { field: 'key_product_code', headerName: 'Product Code', width: 160 },
    { field: 'key_registration_number', headerName: 'Reg. Number', width: 160 },
    { field: 'key_k_number', headerName: '510(k)', width: 130 },
    { field: 'key_pma_number', headerName: 'PMA', width: 130 },
    { field: 'product_created_date', headerName: 'Created Date', width: 140 },
  ];

  const UdiColumns: GridColDef<FdaUdiModel>[] = [
    { field: 'public_device_record_key', headerName: 'UDI Key', width: 160 },
    { field: 'device_description', headerName: 'Device Description', width: 300 },
    { field: 'company_name', headerName: 'Company', width: 200 },
    { field: 'is_single_use', headerName: 'Single Use', width: 130 },
    { field: 'is_rx', headerName: 'Rx Only', width: 100 },
    { field: 'is_otc', headerName: 'OTC', width: 100 },
  ];

  const tabs = [
    {
      label: '510 K',
      rows: k510?.map((a, i) => ({ id: i, ...a })) || [],
      columns: k510Columns,
      modalRenderer: (row: Fda510kModel) => (
        <K510Modal
          searchExecutionId={searchExecutionId}
          selected_510k={row}
        />
      ),
    },
    {
      label: 'Enforcements',
      rows: enforcements?.map((a, i) => ({ id: i, ...a })) || [],
      columns: EnforcementColumns,
      modalRenderer: (row: FdaEnforcementModel) => (
        <EnforcementModal
          searchExecutionId={searchExecutionId}
          selected_enforcement={row}
        />
      ),
    },
    {
      label: 'Events',
      rows: events?.map((a, i) => ({ id: i, ...a })) || [],
      columns: EventColumns,
      modalRenderer: (row: FdaEventModel) => (
        <FdaEventModal
          searchExecutionId={searchExecutionId}
          selected_event={row}
        />
      ),
    },
    {
      label: 'PMA',
      rows: pma?.map((a, i) => ({ id: i, ...a })) || [],
      columns: PmaColumns,
      modalRenderer: (row: FdaPmaModel) => (
        <PmaModal
          searchExecutionId={searchExecutionId}
          selected_pma={row}
        />
      ),
    },
    {
      label: 'Recalls',
      rows: recalls?.map((a, i) => ({ id: i, ...a })) || [],
      columns: RecallColumns,
      modalRenderer: (row: FdaRecallModel) => (
        <RecallModal
          searchExecutionId={searchExecutionId}
          selected_recall={row}
        />
      ),
    },
    {
      label: 'Reg. & Listing',
      rows: registration_listings?.map((a, i) => ({ id: i, ...a })) || [],
      columns: RegistrationListingColumns,
      modalRenderer: (row: FdaRegistrationListingModel) => (
        <RegistrationListingModal
          searchExecutionId={searchExecutionId}
          selected_registration={row}
        />
      ),
    },
    {
      label: 'UDI',
      rows: udi?.map((a, i) => ({ id: i, ...a })) || [],
      columns: UdiColumns,
      modalRenderer: (row: FdaUdiModel) => (
        <UdiModal
          searchExecutionId={searchExecutionId}
          selected_udi={row}
        />
      ),
    },
  ];

  return (
    <Box sx={{ height: 'calc(100vh - 150px)', width: '100%' }}>
      <StandardDataGrid isLoading={isLoading} tabs={tabs} />
    </Box>
  );
};

export default FDA;
